import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setProductDiscount,
  removeProductDiscount,
  getProductsStatus,
  getProductsError,
  getProductsSuccess,
  updateStatus,
} from "../../../features/productSlice";
import { toast } from "react-toastify";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import { formatDateTime } from "../../../global/functions/dateFormat";
import CustomDialog from "../../../components/cutomPopUp";
import DatepickerFilter from "../../../components/filters/datepickerFilter";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const SetDiscountDialog = ({ open, onClose, product }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId, currency } = useRestaurant();
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const startDate = new Date();
  const endDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  const [discountValue, setDiscountValue] = useState("");
  const [discountStartDate, setDiscountStartDate] = useState("");
  const [discountEndDate, setDiscountEndDate] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: startDate, endDate: endDate });
  const status = useSelector(getProductsStatus);
  const error = useSelector(getProductsError);
  const success = useSelector(getProductsSuccess);


  useEffect(() => {
    if (product) {
      setDiscountValue(product.discountValue || "");
      setDiscountStartDate(product.discountStartDate ? product.discountStartDate.split('T')[0] : "");
      setDiscountEndDate(product.discountEndDate ? product.discountEndDate.split('T')[0] : "");
    }
  }, [product]);

  useEffect(() => {
    if (status === "discountSetSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      onClose();
    } else if (status === "discountSetError") {
      toast.error(error);
      dispatch(updateStatus());
    } else if (status === "discountRemoveSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      onClose();
    } else if (status === "discountRemoveError") {
      toast.error(error);
      dispatch(updateStatus());
    }
  }, [status, success, error, dispatch, onClose]);

  const handleSetDiscount = () => {
    if (!discountValue || discountValue <= 0) {
      toast.error("Veuillez entrer une valeur de remise valide");
      return;
    }

    const body = {
      discountValue: parseFloat(discountValue),
    };

    if (discountStartDate) {
      body.discountStartDate = discountStartDate;
    }

    if (discountEndDate) {
      body.discountEndDate = discountEndDate;
    }

    dispatch(
      setProductDiscount({
        productId: product._id,
        body,
        restaurantId,
      })
    );
  };

  const handleRemoveDiscount = () => {
    dispatch(
      removeProductDiscount({
        productId: product._id,
        restaurantId,
      })
    );
  };

  const handleClose = () => {
    setDiscountValue("");
    setDiscountStartDate("");
    setDiscountEndDate("");
    onClose();
  };

  if (!product) return null;

  const hasDiscount = Boolean(product.discountValue && product.discountValue > 0);
  const currentPrice = Number(product.price) || 0;
  const originalPrice = Number(product.originalPrice) || currentPrice;
  const discountedPrice = hasDiscount ? (Number(product.discountedPrice) || (originalPrice - Number(product.discountValue))) : currentPrice;
  const discountPercentage = hasDiscount && originalPrice > 0
    ? ((Number(product.discountValue) / originalPrice) * 100).toFixed(1)
    : 0;

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title={`Gestion de la remise - ${product.name}`}
      message={`${t("fields.currentPrice")}: ${hasDiscount ? discountedPrice.toFixed(2) : currentPrice.toFixed(2)} ${currency}`}
      confirmText={hasDiscount ? "Modifier la remise" : "Appliquer la remise"}
      disabled={status === "loading"}
      onClick={handleSetDiscount}
    >
      <Box sx={{ mb: 2 }}>
        {hasDiscount && originalPrice > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              {t("fields.originalPrice")}: {originalPrice.toFixed(2)} {currency}
            </Typography>
            <Chip
              size="small"
              label={`-${discountPercentage}%`}
              color="success"
            />
          </Stack>
        )}
      </Box>
      {hasDiscount && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t("fields.activeDiscount")}
            {product.discountStartDate && product.discountEndDate && (
              <>
                <br />
                {t("fields.period")}: {formatDateTime(product.discountStartDate)} - {formatDateTime(product.discountEndDate)}
              </>
            )}
          </Typography>
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label={t("fields.discountValue", { currency })}
          type="number"
          fullWidth
          value={discountValue}
          onChange={(e) => setDiscountValue(e.target.value)}
          inputProps={{ min: 0, step: 0.01 }}
          helperText={t("fields.discountHelper")}
        />
        <DatepickerFilter
          label={t("filters.startDate")}
          value={dateRange.startDate}
          onChange={(newValue) => setDateRange({ ...dateRange, startDate: newValue })}
          isDark={isLightMode}
          maxDate={
            dateRange.endDate && dayjs(dateRange.endDate).isBefore(dayjs())
              ? dayjs(dateRange.endDate)
              : dayjs()
          }
        />
        <DatepickerFilter
          label={t("filters.endDate")}
          value={dateRange.endDate}
          onChange={(newValue) => setDateRange({ ...dateRange, endDate: newValue })}
          isDark={isLightMode}
          minDate={dateRange.startDate ? dayjs(dateRange.startDate) : null}
          maxDate={dayjs()}
        />
        {/* <TextField
          label="Date de début (optionnel)"
          type="date"
          fullWidth
          value={discountStartDate}
          onChange={(e) => setDiscountStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Date de fin (optionnel)"
          type="date"
          fullWidth
          value={discountEndDate}
          onChange={(e) => setDiscountEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        /> */}
        {discountValue && (
          <Alert severity="success">
            {t("fields.priceAfterDiscount")}: {(currentPrice - parseFloat(discountValue || 0)).toFixed(2)} {currency}
          </Alert>
        )}
      </Box>
      {hasDiscount && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={handleRemoveDiscount}
            color="error"
            variant="outlined"
            disabled={status === "loading"}
          >
            Supprimer la remise
          </Button>
        </Box>
      )}
    </CustomDialog>
  );
};

export default SetDiscountDialog;
